import React, {useContext, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {
    IButtonProps,
    Icon,
    IDataInfoProps,
    IFilterProps,
    ITextFieldProps,
    ModalProvider,
    TemplateCompany,
    TFilterValue,
    Toggle,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {WebappBase} from '../dashboard/WebappBase';
import {
    CompanyPageStore,
    CompanyPageTab,
    EmployeeDataInBusinessUnit,
    OneBusinessUnit,
    OneMatrixTeam,
} from './company-page-store';
import {
    ITemplateCompanyAction,
    ITemplateCompanyRow,
    ITemplateCompanyTable,
} from '../../../patterns/templates/company/company';
import {AddAndEditEmployeeModal} from './AddAndEditEmployeeModal';
import {AddAndEditDepartmentModal} from './AddAndEditDepartmentModal';
import {UserStore} from '../user/user-store';
import {CompanyPageHelpModal} from './CompanyPageHelpModal';
import {UploadEmployeesModal} from './UploadEmployeesModal';
import {EditCompanySettingsModal} from './EditCompanySettingsModal';
import {EditCompanyLanguagesModal} from './EditCompanyLanguagesModal';
import {AddAndEditMatrixTeamModal} from './AddAndEditMatrixTeamModal';
import {EditCompanyContactsModal} from './EditCompanyContactsModal';
import {EditTargetsModal} from './EditTargetsModal';


export const CompanyPage = observer((): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const userStore = useContext(StoreContext).userStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    return (
        <WebappBase
            store={store}
            title={store.label('company.title')}
            renderChildren={(): React.ReactNode => {

                let addDepartmentOrPeople;

                if (store.variables.openTab === 'people') {
                    addDepartmentOrPeople = addEmployeeButtonProps(store);
                } else if (store.variables.openTab === 'matrix') {
                    addDepartmentOrPeople = addMatrixTeamButtonProps(store);
                } else {
                    addDepartmentOrPeople = addBusinessUnitButtonProps(store);
                }

                const companyPageHelpModal: ITemplateCompanyAction = {
                    options: [
                        {
                            text: '',
                            icon: 'info',
                            color: 'white',
                            onClick: (): void => store.openCompanyPageHelpModal(),
                        },
                    ],
                };

                const companyPageAdminActions: ITemplateCompanyAction = {
                    options: [
                        {
                            text: store.label('company.actions.help'),
                            iconLeft: 'info',
                            onClick: (): void => store.openCompanyPageHelpModal(),
                        },
                        {
                            text: store.label('company.actions.contacts'),
                            iconLeft: 'settings',
                            onClick: (): void => store.openEditCompanyContactsModal(store.payload!.companySettingsPayload),
                        },
                        {
                            text: store.label('company.actions.settings'),
                            iconLeft: 'settings',
                            onClick: (): void => store.openEditCompanySettingsModal(store.payload!.companySettingsPayload),
                        },
                        {
                            text: store.label('company.actions.language-settings'),
                            iconLeft: 'settings',
                            onClick: (): void => store.openEditCompanyLanguageModal(store.payload!.companySettingsPayload),
                        },
                        {
                            text: store.label('company.actions.target-settings'),
                            iconLeft: 'settings',
                            onClick: (): void => store.openEditTargetsModal(store.payload!.companySettingsPayload),
                        },
                        {
                            iconLeft: 'slack-colorful',
                            text: store.label('company.company-modal.add-slack'),
                            onClick: (): void => store.startSlackInstall(),
                        },
                        {
                            iconLeft: 'microsoft-teams',
                            text: store.label('company.company-modal.add-teams'),
                            onClick: (): void => store.sendTeamsInstallInstructions(),
                        },
                        {
                            text: store.label('company.actions.upload'),
                            iconLeft: 'file-upload',
                            onClick: (): void => store.openUploadEmployeesModal(),
                        },
                        {
                            text: store.label('company.actions.delete'),
                            iconLeft: 'delete',
                            onClick: (): void => store.deleteCompany(store.payload!.companySettingsPayload),
                        },
                    ],
                };

                let isOpen;

                if (store.variables.openTab === 'people' && store.payload!.allEmployeesPayload.allEmployees.length < 2) {
                    isOpen = true;
                } else {
                    isOpen = store.variables.openTab === 'structure' && store.payload!.rootOneBusinessUnit.children.length < 1;
                }

                let companyTable;

                if (store.variables.openTab === 'people') {
                    companyTable = companyPeopleTableProps(store, userStore);
                } else if (store.variables.openTab === 'matrix') {
                    companyTable = companyMatrixTableProps(store, userStore);
                } else {
                    companyTable = companyStructureTableProps(store, userStore);
                }

                let titleTooltipPayload;

                if (store.variables.openTab === 'people') {
                    titleTooltipPayload = store.label('company.people.tooltip');
                } else if (store.variables.openTab === 'structure') {
                    titleTooltipPayload = store.label('company.structure.tooltip');
                } else {
                    titleTooltipPayload = undefined;
                }

                let addAction;

                if (userStore.payload?.isAdmin) {
                    addAction = addDepartmentOrPeople;
                } else if (store.variables.openTab === 'people' && userStore.payload?.isManagerOrPartner) {
                    addAction = addDepartmentOrPeople;
                } else {
                    addAction = undefined;
                }

                return (
                    <React.Fragment>
                        <ModalProvider
                            isOpen={store.variables.openModal !== undefined}
                            onToggle={(): void => store.closeModal()}>
                            {store.variables.openModal === 'add-employee' && <AddAndEditEmployeeModal/>}
                            {store.variables.openModal === 'edit-employee' && <AddAndEditEmployeeModal/>}
                            {store.variables.openModal === 'add-department' && <AddAndEditDepartmentModal/>}
                            {store.variables.openModal === 'edit-department' && <AddAndEditDepartmentModal/>}
                            {store.variables.openModal === 'add-matrix-team' && <AddAndEditMatrixTeamModal/>}
                            {store.variables.openModal === 'edit-matrix-team' && <AddAndEditMatrixTeamModal/>}
                            {store.variables.openModal === 'edit-company-contacts' && <EditCompanyContactsModal/>}
                            {store.variables.openModal === 'edit-company-settings' && <EditCompanySettingsModal/>}
                            {store.variables.openModal === 'edit-company-languages' && <EditCompanyLanguagesModal/>}
                            {store.variables.openModal === 'edit-targets' && <EditTargetsModal/>}
                            {store.variables.openModal === 'company-page-help' && <CompanyPageHelpModal/>}
                            {store.variables.openModal === 'upload-employees' && <UploadEmployeesModal/>}
                        </ModalProvider>
                        <TemplateCompany
                            title={store.label('company.title')}
                            toolbar={{
                                add: addAction,
                                titleTooltip: titleTooltipPayload,
                                defaultIsOpen: isOpen,
                                action: userStore.payload?.isAdmin === true ? companyPageAdminActions : companyPageHelpModal,
                            }}
                            filter={companyFilterProps(store)}
                            search={store.variables.openTab === 'people' ? companySearchProps(store) : undefined}
                            table={companyTable}
                        />
                    </React.Fragment>
                );
            }
            }/>
    );
});

const addEmployeeButtonProps = (store: CompanyPageStore): IButtonProps => ({
    text: store.label('company.people.add-employee-button'),
    onClick: (): void => store.openAddEmployeeModal(),
});

const addBusinessUnitButtonProps = (store: CompanyPageStore): IButtonProps => ({
    text: store.label('company.structure.add-business-unit-button'),
    onClick: (): void => store.openAddDepartmentModal(),
});

const addMatrixTeamButtonProps = (store: CompanyPageStore): IButtonProps => ({
    text: store.label('company.matrix.add-matrix-team-button'),
    onClick: (): void => store.openAddMatrixTeamModal(),
});

const companySearchProps = (store: CompanyPageStore): ITextFieldProps => ({
    label: store.label('company.filter.search.label'),
    id: 'company-text-1',
    name: 'companysearch',
    value: store.variables.searchKey,
    onChange: (value: string): void => store.onSearchKeyChange(value),
    focusLabel: store.label('company.filter.search.focuslabel'),
});

const companyFilterProps = (store:CompanyPageStore): IFilterProps => ({
    label: 'Filter label',
    onChange: (value: TFilterValue): void => store.onCompanyTabChange(value as CompanyPageTab),
    value: store.variables.openTab,

    items: [
        {
            id: 'filter-1',
            name: 'filter',
            label: store.label('company.filter.structure'),
            value: 'structure',
        },
        {
            id: 'filter-2',
            name: 'filter',
            label: store.label('company.filter.people'),
            value: 'people',
            count: store.payload!.allEmployeesPayload.allEmployees.length ? store.payload!.allEmployeesPayload.allEmployees.length : undefined,
        },
        {
            id: 'filter-3',
            name: 'filter',
            label: store.label('company.filter.matrix'),
            value: 'matrix',
        },
    ],
});

const companyStructureTableProps = (store: CompanyPageStore, userStore: UserStore): ITemplateCompanyTable => {
    const rows: ITemplateCompanyRow[] = [businessUnitProps(store.payload!.rootOneBusinessUnit, store, userStore, false)];

    return ({
        columns: [
            {
                header: store.label('company.structure.subheader.department'),
                width: '75%',
            },
            {
                header: store.label('company.structure.subheader.manager'),
                width: '15%',
            },
            {
                header: userStore.payload?.isAdmin === true ? store.label('company.structure.subheader.isActive') : '',
                width: '10%',
            },
        ],
        rows,
        defaultOpenItems: [store.payload!.rootOneBusinessUnit.id],
    });
};

const companyMatrixTableProps = (store: CompanyPageStore, userStore: UserStore): ITemplateCompanyTable => {
    const rows: ITemplateCompanyRow[] = store.payload!.matrixTeamsPayload.map((team) => (matrixTeamProps(team, store, userStore)));

    return ({
        columns: [
            {
                header: store.label('company.matrix.subheader.team'),
                width: '70%',
            },
            {
                header: store.label('company.matrix.subheader.manager'),
                width: '30%',
            },
        ],
        rows: rows.length > 0 ? rows : {
            type: 'info',
            text: store.label('company.matrix.no-data'),
        } as IDataInfoProps,
        defaultOpenItems: store.payload!.matrixTeamsPayload.length > 0 ? [store.payload!.matrixTeamsPayload[0].matrixTeamId] : undefined,
    });
};

const businessUnitProps = (oneBusinessUnit: OneBusinessUnit, store: CompanyPageStore, userStore: UserStore, isMatrixTeam: boolean): ITemplateCompanyRow => {

    const editOrDeleteActions = {
        trigger: {
            text: 'More',
        },
        options: [
            {
                text: store.label('company.department.edit-button'),
                iconLeft: 'settings',
                onClick: (): void => store.openEditDepartmentModal(oneBusinessUnit),
            },
            {
                text: store.label('company.department.delete-button'),
                iconLeft: 'delete',
                onClick: (): void => store.businessUnitEligibleForDeletion(oneBusinessUnit) ? store.deleteDepartment(oneBusinessUnit) : window.alert(store.label('company.department.delete-alert')),
            },
        ],
    };

    return ({
        id: oneBusinessUnit.id,
        activeCount: oneBusinessUnit.activeEmployeeCount && oneBusinessUnit.activeEmployeeCount > 0 ? oneBusinessUnit.activeEmployeeCount : undefined,
        inActiveCount: oneBusinessUnit.inActiveEmployeeCount && oneBusinessUnit.inActiveEmployeeCount > 0 ? oneBusinessUnit.inActiveEmployeeCount : undefined,
        clickableFunction: () => store.onCompanyTabChange('people', oneBusinessUnit.businessUnitName),
        cells: [
            {
                content: oneBusinessUnit.businessUnitName,
            },
            {
                content: oneBusinessUnit.managerName,
            },
            {
                content: userStore.payload?.isAdmin === true && !isMatrixTeam ? <Toggle
                    input={{
                        onChange: (event): void => {
                            store.toggleBusinessUnit(oneBusinessUnit.id, event.target.checked, oneBusinessUnit.isActive);
                        },
                        checked: oneBusinessUnit.requestedIsActive,
                    }}
                    id={oneBusinessUnit.id}
                    isPending={oneBusinessUnit.isActive !== oneBusinessUnit.requestedIsActive}
                /> : undefined,
            },
        ],
        actions: userStore.payload?.isAdmin && !isMatrixTeam ? editOrDeleteActions : undefined,
        subRows: oneBusinessUnit.children.map((u) => businessUnitProps(u, store, userStore, isMatrixTeam)),
    });
};

const matrixTeamProps = (oneMatrixTeam: OneMatrixTeam, store: CompanyPageStore, userStore: UserStore): ITemplateCompanyRow => {

    const editOrDeleteActions = {
        trigger: {
            text: 'More',
        },
        options: [
            {
                text: store.label('company.department.edit-button'),
                iconLeft: 'settings',
                onClick: (): void => store.openEditMatrixTeamModal(oneMatrixTeam),
            },
            {
                text: store.label('company.department.delete-button'),
                iconLeft: 'delete',
                onClick: (): void => store.deleteMatrixTeam(oneMatrixTeam),
            },
        ],
    };

    return ({
        id: oneMatrixTeam.matrixTeamId,
        activeCount: oneMatrixTeam.activeEmployeeCount && oneMatrixTeam.activeEmployeeCount > 0 ? oneMatrixTeam.activeEmployeeCount : undefined,
        inActiveCount: oneMatrixTeam.inActiveEmployeeCount && oneMatrixTeam.inActiveEmployeeCount > 0 ? oneMatrixTeam.inActiveEmployeeCount : undefined,
        cells: [
            {
                content: oneMatrixTeam.matrixTeamName,
            },
            {
                content: oneMatrixTeam.managerName,
            },
        ],
        actions: userStore.payload?.isAdmin ? editOrDeleteActions : undefined,
        subRows: oneMatrixTeam.children.map((childTeam) => businessUnitProps(childTeam, store, userStore, true)),
    });
};

const companyPeopleTableProps = (store: CompanyPageStore, userStore: UserStore): ITemplateCompanyTable => {
    const hasTag = store.getEmployees().some((employee) => employee.employeeTags.length > 0);

    const rows: ITemplateCompanyRow[] = store.getEmployees().map((employeeDataInBusinessUnit: EmployeeDataInBusinessUnit): ITemplateCompanyRow => {
        const joinedEmployeeTag = employeeDataInBusinessUnit.employeeTags.join(', ');
        let businessUnitAndParent;

        if (employeeDataInBusinessUnit.parentBusinessUnitName !== undefined) {
            businessUnitAndParent = employeeDataInBusinessUnit.parentBusinessUnitName + ' / ' + employeeDataInBusinessUnit.businessUnitName;
        } else {
            businessUnitAndParent = employeeDataInBusinessUnit.businessUnitName;
        }

        const editOrDeleteActions = {
            trigger: {
                text: 'More',
            },
            options: [
                {
                    text: store.label('company.people.edit-button'),
                    iconLeft: 'settings',
                    onClick: (): void => store.openEditEmployeeModal(employeeDataInBusinessUnit),
                },
                {
                    text: store.label('company.people.reset-onboarding-button'),
                    iconLeft: 'arrow',
                    disabled: employeeDataInBusinessUnit.createdOnboardingsCount > 4 || employeeDataInBusinessUnit.isOnboarded,
                    onClick: (): void => store.resetOnboarding(employeeDataInBusinessUnit),
                },
                {
                    text: store.label('company.people.delete-button'),
                    iconLeft: 'delete',
                    onClick: (): void => store.employeeEligibleForDeletion(store.payload!.rootOneBusinessUnit, employeeDataInBusinessUnit) ? store.deleteEmployee(employeeDataInBusinessUnit) : window.alert(store.label('company.employee.delete-alert')),
                },
            ],
        };

        let role = '';

        if (employeeDataInBusinessUnit.isAdmin === true) {
            role = 'admin';
        } else if (employeeDataInBusinessUnit.isManager === true) {
            role = 'manager';
        } else if (employeeDataInBusinessUnit.isPartner === true) {
            role = 'employee-partner';
        } else {
            role = 'employee';
        }

        return (hasTag ? {
            id: employeeDataInBusinessUnit.employeeId,
            cells: [
                {
                    content: employeeDataInBusinessUnit.employeeFirstName.concat(' '+ employeeDataInBusinessUnit.employeeLastName),
                    comparableValue: employeeDataInBusinessUnit.employeeFirstName,
                },
                {
                    content: employeeDataInBusinessUnit.position,
                    comparableValue: employeeDataInBusinessUnit.position,
                },
                {
                    content: businessUnitAndParent,
                    comparableValue: businessUnitAndParent,
                },
                {
                    content: joinedEmployeeTag,
                    comparableValue: joinedEmployeeTag,
                },
                {
                    content: store.label(`common.location.${employeeDataInBusinessUnit.locationId}`),
                    comparableValue: employeeDataInBusinessUnit.locationId,
                },
                {
                    content: store.label(`company.people.rights.${role}`),
                    comparableValue: role,
                },
                {
                    content: employeeDataInBusinessUnit.isOnboarded === true? <Icon name={'check-green'} className={'table__icon'}/>: <Icon name={'x-red'} className={'table__icon'}/>,
                    comparableValue: employeeDataInBusinessUnit.isOnboarded === true ? 1 : 0,
                },
            ],
            actions: userStore.payload?.isAdmin || userStore.payload?.isManagerOrPartner ? editOrDeleteActions : undefined,
        } : {
            id: employeeDataInBusinessUnit.employeeId,
            cells: [
                {
                    content: employeeDataInBusinessUnit.employeeFirstName.concat(' '+ employeeDataInBusinessUnit.employeeLastName),
                    comparableValue: employeeDataInBusinessUnit.employeeFirstName,
                },
                {
                    content: employeeDataInBusinessUnit.position,
                    comparableValue: employeeDataInBusinessUnit.position,
                },
                {
                    content: businessUnitAndParent,
                    comparableValue: businessUnitAndParent,
                },
                {
                    content: store.label(`common.location.${employeeDataInBusinessUnit.locationId}`),
                    comparableValue: employeeDataInBusinessUnit.locationId,
                },
                {
                    content: store.label(`company.people.rights.${role}`),
                    comparableValue: role,
                },
                {
                    content: employeeDataInBusinessUnit.isOnboarded === true? <Icon name={'check-green'} className={'table__icon'}/>: <Icon name={'x-red'} className={'table__icon'}/>,
                    comparableValue: employeeDataInBusinessUnit.isOnboarded === true ? 1 : 0,
                },
            ],
            actions: userStore.payload?.isAdmin || userStore.payload?.isManagerOrPartner ? editOrDeleteActions : undefined,
        });
    });

    return (hasTag ? {
        columns: [
            {
                header: store.label('company.people.subheader.employee'),
                width: '12.5%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.position'),
                width: '15%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.department'),
                width: '30%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.tag'),
                width: '12.5%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.location'),
                width: '10%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.rights'),
                width: '10%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.active'),
                width: '10%',
                isSortable: true,
            },
        ],
        rows: rows.length > 0 ? rows : {
            type: 'info',
            text: store.label('company.filter.search.no-match'),
        } as IDataInfoProps,
    } : {
        columns: [
            {
                header: store.label('company.people.subheader.employee'),
                width: '15%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.position'),
                width: '20%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.department'),
                width: '30%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.location'),
                width: '10%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.rights'),
                width: '10%',
                isSortable: true,
            },
            {
                header: store.label('company.people.subheader.active'),
                width: '15%',
                isSortable: true,
            },
        ],
        rows: rows.length > 0 ? rows : {
            type: 'info',
            text: store.label('company.filter.search.no-match'),
        } as IDataInfoProps,
    });
};

